import { useCallback, useState } from "react"

// libraries
import { debounce } from "lodash"
import ClickAwayListener from "react-click-away-listener"

// helpers
import Api from "@/_helpers/Api"
import { Link } from "react-router-dom"
import Skeleton from "react-loading-skeleton"
import { trans } from "@/_providers/Translation"

// Как ще работи:
// След като заявката върне резултати (класовете), за всеки клас (напр. Partner, Supplier и тн.) ще имам предварително дефиниран route в React. Когато потребителя кликне на някой от резултатите, ще го изпращам към {route name}?from_global_search={id}, като в конкретната страница ще взимам id-то от GET параметъра и ще го поплвам в полето за търсене по следния начин: id:{id} (напр. id:123). В в бекенда ще търсиш, дали параметъра съдържа "id:", ако да - взимаш разделяш стринга, всимаш дясната част и ми връщаш резултат с конкретното ID
export default function GlobalSearch() {
  const CLASSES = {
    Partner: {
      name: "Партньори",
      reactRoute: "/relationships",
      searchFieldName: "name",
    },
    Todo: {
      name: "Бележки",
      reactRoute: "/todo",
      searchFieldName: "text",
    },
    Address: {
      name: "Адреси",
      reactRoute: "/company?tab=settings&sub=addresses",
      searchFieldName: "name",
    },
    ApplianceBrand: {
      name: "Марки",
      reactRoute: "/company?tab=settings&sub=brands",
      searchFieldName: "name",
    },
    ApplianceCategory: {
      name: "Категории",
      reactRoute: "/company?tab=settings&sub=categories",
      searchFieldName: "name",
    },
    Van: {
      name: "Ванове",
      reactRoute: "/company?tab=settings&sub=vans",
      searchFieldName: "name",
    },
    Storages: {
      name: "Складове",
      reactRoute: "/company?tab=settings&sub=storages",
      searchFieldName: "name",
    },
    StorageStatus: {
      name: "Статуси в склада",
      reactRoute: "/company?tab=settings&sub=storagestatuses",
    },
    Supplier: {
      name: "Доставчици",
      reactRoute: "/parts/suppliers",
      searchFieldName: "name",
    },
    Product: {
      name: "Продукти",
      reactRoute: "/parts/storage/office",
      searchFieldName: "name",
    },
    Reservation: {
      name: "Резервации",
      reactRoute: "/schedule/reservations",
      searchFieldName: "search",
    },
    Employee: {
      name: "Служители",
      reactRoute: "/company?tab=employees",
      searchFieldName: "name",
    },
    Contractor: {
      name: "Договорни компании",
      reactRoute: "/contractors?tab=list",
      searchFieldName: "name",
    },
    ContractorType: {
      name: "Типове договорни компании",
      reactRoute: "/contractors?tab=types&types=companies",
      searchFieldName: "name",
    },
    EmployeeGroup: {
      name: "Групи служители",
      reactRoute: "/company?tab=groups",
      searchFieldName: "name",
    },
    ReservationType: {
      name: "Типове резервации",
      reactRoute: "/company?tab=settings&sub=reservationstypes",
      searchFieldName: "name",
    },
    FeedbackEmail: {
      name: "Имейл за обратна връзка от клиенти",
      reactRoute: "/company?tab=settings&sub=feedbackemail",
      searchFieldName: null,
    },
    TextMessage: {
      name: "Текстови съобщения",
      reactRoute: "/communication",
      searchFieldName: "text",
    },
    OrderedPart: {
      name: "Използвани части",
      reactRoute: "/parts/storage",
      searchFieldName: "name",
    },
    SalesOrder: {
      name: "Онлайн Поръчки",
      reactRoute: "/parts/online-sales?tab=all",
      searchFieldName: "search",
    },
    Invoice: {
      name: "Фактури",
      reactRoute: "/accounting",
      searchFieldName: "search",
    },
  }

  const [results, setResults] = useState({})
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)

  const hasResults = () => {
    return Object.values(results).some((arr) => arr.length > 0)
  }

  const debouncedSearch = useCallback(
    debounce((searchValue) => {
      if (searchValue) {
        setLoading(true)
        setVisible(true)

        Api.get(`/global-search?q=${encodeURIComponent(searchValue)}`)
          .then((res) => {
            setResults(Array.isArray(res.data) ? {} : res.data || {})
          })
          .catch((error) => {
            console.error("Search error:", error)
          })
          .finally(() => {
            setLoading(false)
          })
      } else {
        setResults({})
        setVisible(false)
        setLoading(false)
      }
    }, 300),
    [],
  )

  const handleChange = (e) => {
    debouncedSearch(e.target.value)
  }

  const handleFocus = () => {
    if (hasResults()) {
      setVisible(true)
    }
  }

  function getIfAnyResults() {
    return Object.values(results).some((arr) => arr.length > 0)
  }

  return (
    <ClickAwayListener
      onClickAway={() => setVisible(false)}
      className="click-away"
    >
      <div className={`global-search-container ${visible ? "shown" : ""}`}>
        <input
          type="search"
          name="search"
          placeholder="Търсене в системата..."
          onChange={handleChange}
          onFocus={handleFocus}
          autoComplete="off"
        />
        {visible ? (
          <div className="global-search-dropdown">
            <h3>Резултати от търсенето</h3>
            <hr />
            {loading ? (
              <Skeleton height={45} count={5} />
            ) : getIfAnyResults() ? (
              <ul className="main-list">
                {Object.keys(CLASSES)
                  .filter(
                    (classKey) =>
                      results[classKey] && results[classKey].length > 0,
                  )
                  .map((classKey) => (
                    <li key={classKey}>
                      <b>{CLASSES[classKey].name}</b>
                      <ul>
                        {(results[classKey] || []).map((item) => (
                          <li key={item.id}>
                            <Link
                              to={`${CLASSES[classKey].reactRoute}${CLASSES[classKey].searchFieldName ? `${CLASSES[classKey].reactRoute.includes("?") ? "&" : "?"}${CLASSES[classKey].searchFieldName}=id:${item.id}` : ""}`}
                              dangerouslySetInnerHTML={{
                                __html: item.matches[0].highlight,
                              }}
                              onClick={() => {
                                setVisible(false)
                              }}
                            />
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
              </ul>
            ) : (
              <div
                className="alert warning"
                style={{
                  marginBottom: 0,
                }}
              >
                {trans("messages.noResultsFound")}
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
      </div>
    </ClickAwayListener>
  )
}
